// T-443 (Food Cost): controllo "un DDT = un carico, una volta sola".
//
// Il percorso storico della edge `fiscal-photo` era append-only ma NON
// idempotente: reimportare lo stesso DDT creava, ad ogni import, un NUOVO
// `document_id` e una NUOVA serie di eventi `stock_in` (invariante #4 — gli
// eventi non si cancellano). Un DDT importato N volte gonfiava quindi la SPESA
// del food cost di N volte (incidente Le Boccerie: DDT "C / 137367" reimportato
// ~6 volte → 26 righe di carico per un solo documento). Il nuovo commit
// StockCommand è idempotente; questo helper resta necessario per leggere senza
// duplicazioni lo storico già acquisito. Regola dell'owner: «anche se viene
// caricato mille volte, il DDT deve essere considerato UNA sola volta».
//
// Questo helper PURO collassa i re-import a livello di LETTURA (non tocca gli
// eventi). L'identità prudente è fornitore + numero normalizzato + anno del
// documento: lo stesso numero può essere riutilizzato da fornitori diversi o
// negli anni successivi. Se manca uno di questi dati non deduplichiamo: è meglio
// mostrare un possibile duplicato che cancellare dalla lettura un acquisto
// legittimo. Fra i carichi della stessa identità si tiene solo la sessione
// d'import PIÙ RECENTE. I carichi manuali senza `document_id` restano invariati.

export interface DdtStockInLike {
  /** Revisione server monotona: fonte di recency primaria. */
  server_rev?: number | null;
  server_ts?: string | null;
  client_ts?: string | null;
  payload?: {
    /** Numero DDT/fattura — l'IDENTITÀ del documento (stabile fra i re-import). */
    document_number?: string | null;
    /** Id della riga `documents`: NUOVO ad ogni import ⇒ identifica la sessione. */
    document_id?: string | null;
    /** Fornitore del documento. Necessario per non fondere numerazioni altrui. */
    supplier_id?: string | null;
    /**
     * Identità commerciale canonica risolta dal chiamante sulle anagrafiche
     * fornitore (P.IVA coerente; fallback nome/alias). È preferita all'id
     * tecnico, perché un re-import storico può avere creato supplier duplicati.
     */
    supplier_identity?: string | null;
    /** Data commerciale ISO; l'anno separa numerazioni che ripartono da zero. */
    document_date?: string | null;
    [k: string]: unknown;
  } | null;
}

export interface DdtSupplierIdentitySource {
  id: string;
  name?: string | null;
  piva?: string | null;
  /** Ragioni sociali/alias storici noti dello stesso soggetto. */
  aliases?: readonly string[] | null;
}

const LEGAL_OR_GENERIC_NAME_TOKENS = new Set([
  'srl',
  'srls',
  'spa',
  'snc',
  'sas',
  'ss',
  'gmbh',
  'ltd',
  'limited',
  'inc',
  'co',
  'company',
  'societa',
  'italia',
  'italiana',
  'group',
  'gruppo',
  'food',
  'foods',
  'service',
  'services',
  'commerciale',
  'distribuzione',
  'pack',
  'market',
  'trading',
  'supply',
]);

function normalizedVatNumber(value: string | null | undefined): string {
  if (!value) return '';
  const compact = value.toUpperCase().replace(/^IT/, '').replace(/\D/g, '');
  // Il vincolo applicativo storico accetta la P.IVA italiana come 11 cifre.
  // Non imponiamo qui il checksum: anagrafiche estere/legacy possono avere un
  // identificativo fiscale a 11 cifre valido per il dominio ma non italiano.
  return /^\d{11}$/.test(compact) ? compact : '';
}

interface SupplierNameProfile {
  compacts: Set<string>;
  tokens: Set<string>;
}

function supplierNameProfile(
  supplier: DdtSupplierIdentitySource,
): SupplierNameProfile {
  const compacts = new Set<string>();
  const tokens = new Set<string>();
  const names = [supplier.name, ...(supplier.aliases ?? [])];
  for (const name of names) {
    if (!name) continue;
    const normalized = name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' e ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    if (!normalized) continue;
    const meaningful = normalized
      .split(/\s+/)
      .filter((token) => (
        token.length >= 3 && !LEGAL_OR_GENERIC_NAME_TOKENS.has(token)
      ));
    if (meaningful.length === 0) continue;
    meaningful.forEach((token) => tokens.add(token));
    compacts.add(meaningful.join(''));
  }
  return { compacts, tokens };
}

function namesAreConsistent(
  left: SupplierNameProfile,
  right: SupplierNameProfile,
): boolean {
  for (const compact of left.compacts) {
    if (right.compacts.has(compact)) return true;
  }
  if (left.tokens.size === 0 || right.tokens.size === 0) return false;
  const [shorter, longer] = left.tokens.size <= right.tokens.size
    ? [left.tokens, right.tokens]
    : [right.tokens, left.tokens];
  // Un solo token comune è troppo lasco ("Rossi Carni" / "Rossi Pesce").
  // Accettiamo una ragione sociale estesa soltanto se contiene per intero
  // quella più corta ("Woerndle" / "Woerndle Interservice").
  return [...shorter].every((token) => longer.has(token));
}

/**
 * Risolve id tecnici duplicati nello stesso soggetto commerciale.
 *
 * - stessa P.IVA: unisce soltanto nomi/alias coerenti, così una P.IVA del
 *   destinatario avvelenata su fornitori diversi non li fonde;
 * - P.IVA mancante su uno dei due: può unire per nome/alias normalizzato;
 * - P.IVA valide ma diverse: fail-open, mai unione;
 * - nessun dato commerciale: identità per id tecnico.
 */
export function canonicalDdtSupplierIdentities(
  suppliers: readonly DdtSupplierIdentitySource[],
): Map<string, string> {
  const rows = suppliers.filter((supplier) => supplier.id.trim().length > 0);
  const parent = rows.map((_, index) => index);
  const vats = rows.map((supplier) => normalizedVatNumber(supplier.piva));
  const profiles = rows.map(supplierNameProfile);
  const componentVats = rows.map((_, index) => (
    vats[index] ? new Set([vats[index]]) : new Set<string>()
  ));

  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root];
    while (parent[index] !== index) {
      const next = parent[index];
      parent[index] = root;
      index = next;
    }
    return root;
  };
  const union = (left: number, right: number): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot === rightRoot) return;
    const mergedVats = new Set([
      ...componentVats[leftRoot],
      ...componentVats[rightRoot],
    ]);
    // Un alias senza P.IVA non deve fare da ponte fra due P.IVA differenti.
    if (mergedVats.size > 1) return;
    parent[rightRoot] = leftRoot;
    componentVats[leftRoot] = mergedVats;
  };

  for (let left = 0; left < rows.length; left += 1) {
    for (let right = left + 1; right < rows.length; right += 1) {
      const leftVat = vats[left];
      const rightVat = vats[right];
      const consistentName = namesAreConsistent(
        profiles[left],
        profiles[right],
      );
      if (leftVat && rightVat) {
        if (
          leftVat === rightVat
          && (
            consistentName
            || profiles[left].compacts.size === 0
            || profiles[right].compacts.size === 0
          )
        ) {
          union(left, right);
        }
      } else if (consistentName) {
        union(left, right);
      }
    }
  }

  const componentRows = new Map<number, number[]>();
  rows.forEach((_, index) => {
    const root = find(index);
    const indexes = componentRows.get(root);
    if (indexes) indexes.push(index);
    else componentRows.set(root, [index]);
  });

  const result = new Map<string, string>();
  for (const indexes of componentRows.values()) {
    const componentVat = indexes
      .map((index) => vats[index])
      .find(Boolean) ?? '';
    const canonicalName = indexes
      .flatMap((index) => [...profiles[index].compacts])
      .sort()[0] ?? '';
    const fallbackId = indexes.map((index) => rows[index].id).sort()[0];
    const identity = componentVat
      ? `piva:${componentVat}|name:${canonicalName || fallbackId}`
      : (canonicalName ? `name:${canonicalName}` : `id:${fallbackId}`);
    indexes.forEach((index) => result.set(rows[index].id, identity));
  }
  return result;
}

/**
 * Numero DDT normalizzato per il confronto: minuscole, senza spazi né `/`.
 * Così "C / 137367", "C/137367" e "c/137367" sono lo stesso documento.
 * Volutamente conservativo: NON rimuove prefissi alfabetici (es. "C") per non
 * fondere numeri diversi (l'unico residuo noto è "126761" vs "C / 126761").
 */
export function normalizeDocNumber(n: string | null | undefined): string {
  if (n == null) return '';
  return n.replace(/\s+/g, '').replace(/\//g, '').toLowerCase();
}

function recency(e: DdtStockInLike): number {
  if (typeof e.server_rev === 'number' && Number.isFinite(e.server_rev)) return e.server_rev;
  const ts = e.server_ts ?? e.client_ts ?? '';
  const t = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(t) ? t : 0;
}

function documentYear(value: string | null | undefined): string {
  if (value == null) return '';
  const match = value.trim().match(/^(\d{4})-\d{2}-\d{2}(?:$|T)/);
  return match?.[1] ?? '';
}

/**
 * Collassa i carichi (`stock_in`) reimportati dallo stesso DDT a uno solo.
 *
 * Chiave d'identità = identità commerciale canonica del fornitore (fallback
 * supplier_id) + numero DDT normalizzato + anno documento. Partecipano al
 * dedup SOLO gli eventi che hanno anche un `document_id` (i re-import
 * automatici). Nel gruppo si tiene esclusivamente la sessione d'import PIÙ
 * RECENTE (il `document_id` dell'evento con recency massima) e si scartano gli
 * altri. Se manca qualsiasi coordinata, l'evento passa invariato.
 *
 * Preserva l'ordine originale degli eventi tenuti. Funzione pura, senza side
 * effect: usata dal calcolo del food cost (spesa, valorizzazione, costo/kg).
 */
export function dedupeStockInByDdt<T extends DdtStockInLike>(events: readonly T[]): T[] {
  const groups = new Map<string, T[]>();
  for (const e of events) {
    const num = normalizeDocNumber(e.payload?.document_number);
    const docId = e.payload?.document_id ?? null;
    const supplierIdentity = e.payload?.supplier_identity?.trim()
      || e.payload?.supplier_id?.trim()
      || '';
    const year = documentYear(e.payload?.document_date);
    if (!num || !docId || !supplierIdentity || !year) continue;
    const identity = `${supplierIdentity}\u0000${year}\u0000${num}`;
    const arr = groups.get(identity);
    if (arr) arr.push(e);
    else groups.set(identity, [e]);
  }

  const dropped = new Set<T>();
  for (const arr of groups.values()) {
    if (arr.length < 2) continue;
    let bestR = -Infinity;
    let winId: string | null = null;
    for (const e of arr) {
      const r = recency(e);
      if (r > bestR) {
        bestR = r;
        winId = e.payload?.document_id ?? null;
      }
    }
    for (const e of arr) {
      if ((e.payload?.document_id ?? null) !== winId) dropped.add(e);
    }
  }

  return dropped.size ? events.filter((e) => !dropped.has(e)) : events.slice();
}
